// src/data/assignments.js
//
// DELEGATED ASSIGNMENTS - 6a-02. Ruling 17 (2026-08-23), specified at
// data\YCP_LC_GC_WORKSPACE_REQUIREMENTS_2026-08-23.md SS5.2.
//
// Bob: "The LCs need a Workshop - where they review, edit, and submit claims, process communications
// and appeals, AS WELL AS ANY OTHER ASSIGNMENTS THE GC MAY GIVE TO THE LC."
//
// ---------------------------------------------------------------------------------------------
// THE SENTENCE THAT DICTATES THE SHAPE, AND IT IS A PROHIBITION ON A DESIGN:
//
//   SS5.2: "'Any other assignments the GC may give to the LC' cannot be enumerated at build time...
//   it requires a first-class assignment record: issuer, assignee, subject, due date, state,
//   response. HARD-CODING A LIST OF ASSIGNMENT TYPES WOULD DEFEAT THE SENTENCE."
//
// SO THERE IS NO `TYPES` CONSTANT IN THIS FILE and there is no `type` field on the record. The
// subject is free text, deliberately, and the suite asserts the absence - because a well-meant
// dropdown of eight assignment kinds is exactly how "any other assignment" becomes "one of these
// eight", and a General Chairperson who needs a ninth is then blocked by his own tool.
//
// WHAT IS DELIBERATELY NOT DECIDED HERE, because Bob has not ruled and inference is not a ruling:
//
//   MAY AN LC DECLINE?     Ruling 18 makes `none` a valid recorded state for a continuity plan, so
//                          the idea of a recorded refusal is not foreign to the platform. But
//                          nothing says an assignment may be declined, and a `declined` state would
//                          be this assistant deciding what authority a General Chairperson has over
//                          a Local Chairperson. It is named at SS-OPEN below and left unbuilt.
//   MAY AN LC ISSUE ONE?   SS5.2 describes GC -> LC only. mayIssue() reflects exactly that and
//                          nothing wider.
// ---------------------------------------------------------------------------------------------

// THE STATES. Four, and every one is a fact somebody caused - not a computed status.
// `overdue` is deliberately NOT among them: it is a function of the due date and today, and storing
// it would mean a row that is overdue only after something remembers to write to it.
export const STATES = {
  ISSUED:       'issued',        // sent, not yet acknowledged
  ACKNOWLEDGED: 'acknowledged',  // the assignee has seen it and taken it
  RESPONDED:    'responded',     // the assignee has answered
  CLOSED:       'closed',        // the ISSUER has closed it - see closeAssignment()
};

export const STATE_ORDER = [STATES.ISSUED, STATES.ACKNOWLEDGED, STATES.RESPONDED, STATES.CLOSED];

export const STATE_LABELS = {
  issued:       'Issued',
  acknowledged: 'Taken up',
  responded:    'Answered',
  closed:       'Closed',
};

export const SUBJECT_MIN = 4;
export const SUBJECT_MAX = 300;
export const RESPONSE_MAX = 2000;

// WHO MAY ISSUE. SS5.2 describes this as how a General Chairperson distributes work across his Local
// Chairpersons, so: the GC of the committee, and the administrator. Nothing wider is inferred.
export function mayIssue(user) {
  if (!user) return false;
  return !!user.isAdmin || (user.role === 'general_chair' && !!user.committee);
}

// WHO MAY ACT ON ONE. The assignee acts on his own; the issuer closes his own. Nobody else touches
// it - an assignment is a two-party record and a third party editing it would make the response
// unattributable.
// MASTER LIST 10.8 (2026-09-18). What the Give-an-assignment seat box shows. ONE decision, tested in
// scripts\test-assignments.mjs, read by AssignmentsPanel.js. The state it adds is 'no-committee': an
// account that may issue but holds no committee - which is the administrator, the one role ruling 45
// exempts from a committee. Before this, that account's seat list was never loaded and never marked
// done, so the box said "Loading..." forever. An unknown is never rendered as a wait that cannot end.
export const SEAT_BOX = Object.freeze({ NO_COMMITTEE: 'no-committee', LOADING: 'loading', EMPTY: 'empty', READY: 'ready' });
export function seatBoxState(user, seats) {
  if (!user || !user.committee) return SEAT_BOX.NO_COMMITTEE;
  if (seats === null || seats === undefined) return SEAT_BOX.LOADING;
  if (!Array.isArray(seats) || seats.length === 0) return SEAT_BOX.EMPTY;
  return SEAT_BOX.READY;
}

export function mayAct(assignment, user) {
  if (!assignment || !user) return false;
  if (user.isAdmin) return true;
  return assignment.assigneeUid === user.uid || assignment.issuerUid === user.uid;
}

export function isAssignee(assignment, user) {
  return !!assignment && !!user && assignment.assigneeUid === user.uid;
}
export function isIssuer(assignment, user) {
  return !!assignment && !!user && assignment.issuerUid === user.uid;
}

// DAYS TO THE DUE DATE. null means NO DUE DATE ON RECORD, and null is common and legitimate - not
// every assignment has a deadline. It must never render as "plenty of time" and never as zero.
// Same rule and the same shape as deskQueue.daysUntilISO, on purpose.
export function daysUntilDue(assignment, nowMs) {
  const iso = assignment && assignment.dueOn;
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return null;
  const d = new Date(String(iso) + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const today = new Date(typeof nowMs === 'number' ? nowMs : Date.now());
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

// OVERDUE IS COMPUTED, NEVER STORED, AND A CLOSED ONE IS NOT OVERDUE.
// An assignment answered late and then closed is finished; carrying it in an overdue list forever
// would train the reader to ignore the list.
export function isOverdue(assignment, nowMs) {
  if (!assignment) return false;
  if (assignment.state === STATES.CLOSED) return false;
  const left = daysUntilDue(assignment, nowMs);
  return left !== null && left < 0;
}

// ---------------------------------------------------------------------------------------------
// ISSUE. Returns { ok, errors[], record } - never a partial record.
// ---------------------------------------------------------------------------------------------
export function issueAssignment(user, input) {
  const i = input || {};
  const errors = [];

  if (!mayIssue(user)) {
    errors.push('Assignments are issued by the General Chairperson to his Local Chairpersons. '
              + 'This account cannot issue one. [R-17]');
  }

  const subject = String(i.subject == null ? '' : i.subject).trim();
  if (!subject) {
    errors.push('Say what the assignment is. There is no list to choose from on purpose - "any other '
              + 'assignment the GC may give" cannot be enumerated in advance.');
  } else if (subject.length < SUBJECT_MIN) {
    errors.push('That is too short to be an assignment (' + subject.length + ' characters).');
  } else if (subject.length > SUBJECT_MAX) {
    errors.push('Keep the subject to ' + SUBJECT_MAX + ' characters. Detail belongs in the body.');
  }

  const assigneeUid = String(i.assigneeUid || '').trim();
  const assigneeName = String(i.assigneeName || '').trim();
  if (!assigneeUid) errors.push('An assignment needs somebody to be assigned to.');
  if (!assigneeName) errors.push('Record the assignee by name as well as by account, so the record '
                              + 'still reads after the account is gone.');

  const dueOn = String(i.dueOn || '').trim();
  if (dueOn && !/^\d{4}-\d{2}-\d{2}$/.test(dueOn)) {
    errors.push('The due date must be a real date (YYYY-MM-DD), or left empty.');
  }

  if (errors.length) return { ok: false, errors, record: null };

  return {
    ok: true,
    errors: [],
    record: {
      subject,
      body: String(i.body || '').trim() || null,
      issuerUid: (user && user.uid) || null,
      issuerName: (user && (user.name || user.email)) || 'Unknown',
      issuerRole: (user && user.role) || 'unknown',
      committee: (user && user.committee) || null,
      assigneeUid,
      assigneeName,
      // NO DUE DATE IS A LEGITIMATE STATE and it is stored as null rather than as a far-off date.
      // An invented deadline is a deadline somebody will be measured against.
      dueOn: dueOn || null,
      // The claim or case this is about, when there is one. Many assignments are about neither.
      subjectClaimId: String(i.subjectClaimId || '').trim() || null,
      state: STATES.ISSUED,
      issuedAt: i.at || new Date().toISOString(),
      acknowledgedAt: null,
      response: null,
      respondedAt: null,
      closedAt: null,
      closedBy: null,
      closeNote: null,
    },
  };
}

// ACKNOWLEDGE - the assignee, and only the assignee, says he has it.
// The issuer cannot acknowledge on his behalf: the whole value of the state is that it reports
// somebody else's act, and an issuer who can set it is an issuer talking to himself.
export function acknowledgePatch(assignment, user, input) {
  const i = input || {};
  const errors = [];
  if (!isAssignee(assignment, user) && !(user && user.isAdmin)) {
    errors.push('Only the person it was assigned to can take it up. An issuer who could acknowledge '
              + 'his own assignment would be recording his own act as somebody else\'s.');
  }
  if (assignment && assignment.state !== STATES.ISSUED) {
    errors.push('This assignment has already been taken up.');
  }
  if (errors.length) return { ok: false, errors, patch: null };
  return { ok: true, errors: [], patch: { state: STATES.ACKNOWLEDGED, acknowledgedAt: i.at || new Date().toISOString() } };
}

// RESPOND - the substance. SS5.2 lists `response` as a field of the record, so the answer lives on
// the assignment rather than in a note somewhere else.
export function respondPatch(assignment, user, input) {
  const i = input || {};
  const errors = [];
  if (!isAssignee(assignment, user) && !(user && user.isAdmin)) {
    errors.push('Only the person it was assigned to can answer it.');
  }
  if (assignment && assignment.state === STATES.CLOSED) {
    errors.push('This assignment is closed.');
  }
  const response = String(i.response == null ? '' : i.response).trim();
  if (!response) errors.push('An empty answer is not an answer. Say what was done, or what stopped it.');
  else if (response.length > RESPONSE_MAX) errors.push('Keep it under ' + RESPONSE_MAX + ' characters.');

  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true,
    errors: [],
    patch: {
      state: STATES.RESPONDED,
      response,
      respondedAt: i.at || new Date().toISOString(),
      // Taking it up is implied by answering it, and an assignment answered without a prior
      // acknowledgement should not read as never taken up.
      acknowledgedAt: (assignment && assignment.acknowledgedAt) || i.at || new Date().toISOString(),
    },
  };
}

// CLOSE - THE ISSUER'S ACT, AND ONLY HIS.
// An assignee who could close his own assignment could mark his own work accepted. The General
// Chairperson gave the work; he says when it is done.
export function closeAssignment(assignment, user, input) {
  const i = input || {};
  const errors = [];
  if (!isIssuer(assignment, user) && !(user && user.isAdmin)) {
    errors.push('Only the officer who issued it can close it. An assignee closing his own assignment '
              + 'would be marking his own work accepted.');
  }
  if (assignment && assignment.state === STATES.CLOSED) errors.push('It is already closed.');
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true,
    errors: [],
    patch: {
      state: STATES.CLOSED,
      closedAt: i.at || new Date().toISOString(),
      closedBy: (user && (user.name || user.email)) || 'Unknown',
      closeNote: String(i.note || '').trim() || null,
    },
  };
}

// ---------------------------------------------------------------------------------------------
// THE QUEUE. Same ordering discipline as the officer's desk: what needs you first, and an unknown
// is never sorted as though it were comfortable.
// ---------------------------------------------------------------------------------------------
export function partitionAssignments(list, opts) {
  const o = opts || {};
  const nowMs = typeof o.nowMs === 'number' ? o.nowMs : Date.now();
  const all = (Array.isArray(list) ? list : []).map(a => ({
    assignment: a,
    left: daysUntilDue(a, nowMs),
    overdue: isOverdue(a, nowMs),
  }));

  const open = all.filter(x => x.assignment && x.assignment.state !== STATES.CLOSED);

  const sortByNeed = (a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    // NO DUE DATE SORTS AFTER EVERY DATED ONE - never first, and never quietly last-but-hidden.
    if (a.left === null && b.left === null) return 0;
    if (a.left === null) return 1;
    if (b.left === null) return -1;
    return a.left - b.left;
  };

  return {
    all,
    open: open.slice().sort(sortByNeed),
    overdue: open.filter(x => x.overdue).sort(sortByNeed),
    awaitingResponse: open.filter(x => x.assignment.state === STATES.ISSUED
                                    || x.assignment.state === STATES.ACKNOWLEDGED).sort(sortByNeed),
    answered: open.filter(x => x.assignment.state === STATES.RESPONDED).sort(sortByNeed),
    // Listed separately so the absence is visible rather than blank - the same treatment
    // deskQueue gives a claim with no filing window.
    noDueDate: open.filter(x => x.left === null),
    closed: all.filter(x => x.assignment && x.assignment.state === STATES.CLOSED),
  };
}
